import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ChaldeanNumerologyCalculator = () => {
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Chaldean Number Meanings
  const chaldeanNumbers = {
    1: {
      power: 'Individual Power',
      traits: [
        'Leader',
        'Independent',
        'Initiator',
        'Creative',
        'Assertive'
      ],
      description: 'Number 1 represents individuality and leadership. People with this number are natural leaders and initiators.'
    },
    2: {
      power: 'Diplomatic Power',
      traits: [
        'Diplomatic',
        'Cooperative',
        'Sensitive',
        'Harmonious',
        'Patient'
      ],
      description: 'Number 2 represents diplomacy and cooperation. People with this number are sensitive and harmonious.'
    },
    3: {
      power: 'Expression Power',
      traits: [
        'Expressive',
        'Optimistic',
        'Creative',
        'Social',
        'Artistic'
      ],
      description: 'Number 3 represents expression and creativity. People with this number are optimistic and artistic.'
    },
    4: {
      power: 'Foundation Power',
      traits: [
        'Practical',
        'Organized',
        'Steadfast',
        'Reliable',
        'Disciplined'
      ],
      description: 'Number 4 represents foundation and practicality. People with this number are organized and reliable.'
    },
    5: {
      power: 'Freedom Power',
      traits: [
        'Adventurous',
        'Curious',
        'Flexible',
        'Resourceful',
        'Progressive'
      ],
      description: 'Number 5 represents freedom and adventure. People with this number are curious and resourceful.'
    },
    6: {
      power: 'Love Power',
      traits: [
        'Loving',
        'Harmonious',
        'Nurturing',
        'Responsible',
        'Balanced'
      ],
      description: 'Number 6 represents love and harmony. People with this number are nurturing and responsible.'
    },
    7: {
      power: 'Spiritual Power',
      traits: [
        'Intuitive',
        'Mystical',
        'Analytical',
        'Solitary',
        'Wise'
      ],
      description: 'Number 7 represents spirituality and wisdom. People with this number are intuitive and analytical.'
    },
    8: {
      power: 'Material Power',
      traits: [
        'Ambitious',
        'Successful',
        'Practical',
        'Powerful',
        'Authoritative'
      ],
      description: 'Number 8 represents material success and power. People with this number are ambitious and successful.'
    },
    9: {
      power: 'Humanitarian Power',
      traits: [
        'Humanitarian',
        'Creative',
        'Idealistic',
        'Generous',
        'Philosophical'
      ],
      description: 'Number 9 represents humanitarian ideals and creativity. People with this number are generous and philosophical.'
    }
  };

  // Chaldean Letter Values
  const chaldeanValues = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 8, G: 3, H: 5, I: 1,
    J: 1, K: 2, L: 3, M: 4, N: 5, O: 7, P: 8, Q: 1, R: 2,
    S: 3, T: 4, U: 6, V: 6, W: 6, X: 5, Y: 1, Z: 7
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const calculateChaldeanNumber = () => {
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    // Convert name to uppercase and remove spaces
    const cleanedName = name.toUpperCase().replace(/\s+/g, '');

    // Calculate Chaldean number
    let total = 0;
    for (let char of cleanedName) {
      if (chaldeanValues[char]) {
        total += chaldeanValues[char];
      }
    }

    // Reduce to single digit
    while (total > 9) {
      total = total.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    }

    const chaldeanData = chaldeanNumbers[total];

    setResults({
      name,
      total,
      power: chaldeanData.power,
      traits: chaldeanData.traits,
      description: chaldeanData.description
    });

    // Prepare chart data
    const chaldeanChartData = {
      labels: ['Power Level', 'Traits', 'Potential'],
      datasets: [{
        label: 'Chaldean Power Distribution',
        data: [60, 30, 10],
        backgroundColor: [
          'rgba(139, 92, 246, 0.8)',
          'rgba(255, 193, 7, 0.8)',
          'rgba(52, 219, 153, 0.8)'
        ],
        borderColor: [
          'rgba(139, 92, 246, 1)',
          'rgba(255, 193, 7, 1)',
          'rgba(52, 219, 153, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 8,
        hoverBackgroundColor: [
          'rgba(139, 92, 246, 0.9)',
          'rgba(255, 193, 7, 0.9)',
          'rgba(52, 219, 153, 0.9)'
        ],
        hoverBorderColor: [
          'rgba(139, 92, 246, 1)',
          'rgba(255, 193, 7, 1)',
          'rgba(52, 219, 153, 1)'
        ]
      }]
    };

    setChartData(chaldeanChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Chaldean Numerology Calculator | Calculate Your Name's Chaldean Number</title>
        <meta name="description" content="Calculate the Chaldean numerology of your name using our Chaldean Numerology Calculator. Understand your personality traits and potential based on ancient Chaldean numerology." />
        <meta name="keywords" content="chaldean numerology, chaldean calculator, personality traits, name analysis, chaldean number calculator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/chaldean-numerology" />
        <meta property="og:title" content="Chaldean Numerology Calculator | Calculate Your Name's Chaldean Number" />
        <meta property="og:description" content="Calculate the Chaldean numerology of your name using our Chaldean Numerology Calculator. Understand your personality traits and potential based on ancient Chaldean numerology." />
        <meta property="og:image" content="https://yourwebsite.com/images/chaldean-numerology-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/chaldean-numerology" />
        <meta property="twitter:title" content="Chaldean Numerology Calculator | Calculate Your Name's Chaldean Number" />
        <meta property="twitter:description" content="Calculate the Chaldean numerology of your name using our Chaldean Numerology Calculator. Understand your personality traits and potential based on ancient Chaldean numerology." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/chaldean-numerology-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔢</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Chaldean Numerology Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Name's Chaldean Number</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Your Name:
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={handleNameChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your name"
          />
        </div>

        <button
          onClick={calculateChaldeanNumber}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Chaldean Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Chaldean Number Analysis</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Name:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.name}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Chaldean Number:</h5>
                    <p className="text-purple-600 text-5xl font-bold">{results.total}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Power Type:</h5>
                    <p className="text-purple-600 text-xl">{results.power}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Key Traits:</h5>
                    <ul className="list-disc pl-6 space-y-2">
                      {results.traits.map((trait, index) => (
                        <li key={index} className="text-gray-700">{trait}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Description:</h5>
                    <p className="text-gray-700">{results.description}</p>
                  </div>
                </div>
              </div>

              {/* Power Visualization Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Power Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Chaldean Power Distribution',
                          color: '#4F46E5',
                          font: {
                            size: 16,
                            weight: 'bold'
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12,
                              weight: 'bold'
                            },
                            padding: 10
                          }
                        }
                      },
                      cutout: '60%',
                      animation: {
                        animateRotate: true,
                        animateScale: true,
                        duration: 1000,
                        easing: 'easeInOutQuart'
                      },
                      layout: {
                        padding: {
                          top: 20,
                          bottom: 30
                        }
                      }
                    }}
                  />
                  
                  {/* Add data labels */}
                  <div className="mt-4 text-center space-y-2">
                    <div className="flex justify-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                        <span className="text-sm text-gray-700">Power Level</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                        <span className="text-sm text-gray-700">Traits</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-sm text-gray-700">Potential</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Number 1 - Individual Power</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Alexander</p>
                <p className="text-purple-600 text-2xl mb-2">Chaldean Number: 1</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name indicates strong individual power, leadership, and creative abilities.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Number 7 - Spiritual Power</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Christopher</p>
                <p className="text-purple-600 text-2xl mb-2">Chaldean Number: 7</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name suggests strong spiritual power, intuition, and analytical abilities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Chaldean numerology:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Name</h3>
              <p className="text-gray-700">
                Input your full name in the input field.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Chaldean Number" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Chaldean number, power type, key traits, and description to understand your personality potential.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Chaldean Numerology?</h3>
              <p className="text-gray-700">
                Chaldean Numerology is an ancient system that assigns specific numerical values to letters based on their vibrational energy. It's considered more accurate than Pythagorean numerology for name analysis.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                The calculator assigns Chaldean numerical values to each letter in your name, sums them up, and reduces the total to a single digit (1-9) to determine your Chaldean number.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does it reveal?</h3>
              <p className="text-gray-700">
                It shows your Chaldean number, which reveals your personality traits, strengths, challenges, and potential based on ancient Chaldean numerological principles.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Chaldean number calculation</li>
                <li>Power type analysis</li>
                <li>Personality trait assessment</li>
                <li>Comprehensive descriptions</li>
                <li>Detailed interpretations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand personality traits</li>
                <li>Make informed decisions</li>
                <li>Enhance self-awareness</li>
                <li>Support personal growth</li>
                <li>Improve relationships</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChaldeanNumerologyCalculator;
